import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { QRCodeCanvas } from 'qrcode.react';
import { QrCode, Download, Copy, Check, X, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Admin-side QR + share-link modal for a given cleaner.
 * Fetches (or creates) the cleaner's referral code and shows a printable QR.
 */
const CleanerReferralQRModal = ({ cleanerId, cleanerName, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [pdfDownloading, setPdfDownloading] = useState(false);
  const qrRef = useRef(null);

  useEffect(() => {
    if (!cleanerId) return;
    const load = async () => {
      try {
        const token = localStorage.getItem('token');
        const r = await axios.get(`${API}/admin/cleaners/${cleanerId}/referral`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setData(r.data);
      } catch (e) {
        setError(e.response?.data?.detail || 'Failed to load referral');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [cleanerId]);

  const copyUrl = () => {
    if (!data?.share_url) return;
    navigator.clipboard.writeText(data.share_url);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const downloadQR = () => {
    if (!qrRef.current) return;
    const canvas = qrRef.current.querySelector('canvas');
    if (!canvas) return;
    const url = canvas.toDataURL('image/png');
    const a = document.createElement('a');
    a.href = url;
    a.download = `matchmycleaner-${(cleanerName || 'cleaner').toLowerCase().replace(/\s+/g, '-')}-${data.code}.png`;
    a.click();
  };

  const printQR = () => window.print();

  const downloadFlyerPdf = async () => {
    setPdfDownloading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/admin/cleaners/${cleanerId}/onboarding-flyer`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `welcome-kit-${(cleanerName || 'cleaner').toLowerCase().replace(/\s+/g, '-')}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert(`Failed to download PDF: ${e.response?.data?.detail || e.message}`);
    } finally {
      setPdfDownloading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 print:bg-white print:relative"
      onClick={onClose}
      data-testid="admin-qr-modal-backdrop"
    >
      <div
        className="bg-surface rounded-2xl shadow-2xl max-w-md w-full print:shadow-none"
        onClick={(e) => e.stopPropagation()}
        data-testid="admin-qr-modal"
      >
        <div className="p-5 border-b border-border flex items-center justify-between print:hidden">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-full bg-primary-light/40">
              <QrCode className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h3 className="font-heading text-lg font-semibold text-text-primary">Referral QR</h3>
              <p className="text-xs text-text-secondary">{cleanerName || 'Cleaner'}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-text-secondary hover:text-text-primary" data-testid="admin-qr-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6">
          {loading && (
            <div className="flex items-center justify-center py-8 gap-2 text-text-secondary" data-testid="admin-qr-loading">
              <Loader2 className="w-5 h-5 animate-spin" /> Loading…
            </div>
          )}
          {error && (
            <p className="text-sm text-rose-700 py-4 text-center" data-testid="admin-qr-error">{error}</p>
          )}
          {data && (
            <div className="flex flex-col items-center gap-4" data-testid="admin-qr-content">
              <div ref={qrRef} className="p-4 bg-white rounded-2xl border-2 border-dashed border-primary/30">
                <QRCodeCanvas
                  value={data.share_url}
                  size={240}
                  fgColor="#1d4f3a"
                  bgColor="#ffffff"
                  level="H"
                  includeMargin
                />
              </div>
              <div className="text-center">
                <p className="text-xs uppercase tracking-wider text-text-secondary font-bold">Referral code</p>
                <p className="font-mono font-bold text-2xl text-primary" data-testid="admin-qr-code">{data.code}</p>
                <p className="text-xs text-text-secondary mt-1">{data.uses_count || 0} referrals so far</p>
              </div>
              <div className="w-full bg-surface-muted rounded-xl p-3 flex items-center justify-between gap-2">
                <p className="text-xs font-mono truncate text-text-primary" data-testid="admin-qr-share-url">{data.share_url}</p>
                <button
                  onClick={copyUrl}
                  className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 bg-surface border border-border rounded-full hover:border-primary hover:text-primary transition"
                  data-testid="admin-qr-copy"
                >
                  {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                  {copied ? 'Copied' : 'Copy'}
                </button>
              </div>
              <div className="grid grid-cols-3 gap-2 w-full print:hidden">
                <button
                  onClick={downloadQR}
                  className="inline-flex items-center justify-center gap-2 text-sm font-semibold px-3 py-2 bg-primary text-white rounded-full hover:bg-primary-dark transition"
                  data-testid="admin-qr-download"
                >
                  <Download className="w-4 h-4" /> QR PNG
                </button>
                <button
                  onClick={downloadFlyerPdf}
                  disabled={pdfDownloading}
                  className="inline-flex items-center justify-center gap-2 text-sm font-semibold px-3 py-2 bg-amber-500 text-white rounded-full hover:bg-amber-600 disabled:opacity-60 transition"
                  data-testid="admin-flyer-download"
                >
                  {pdfDownloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                  Flyer PDF
                </button>
                <button
                  onClick={printQR}
                  className="inline-flex items-center justify-center gap-2 text-sm font-semibold px-3 py-2 bg-surface border border-border rounded-full hover:border-primary hover:text-primary transition"
                  data-testid="admin-qr-print"
                >
                  Print
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CleanerReferralQRModal;
